import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaRegFileAlt, FaDatabase } from "react-icons/fa";
import axios from "axios";
import { MainRoutes } from "../../../config";
import { ConfigurationHubService } from "../../../../modules/configuration-hub/data/services/configuration-hub.service";
import { DatabaseConfiguration } from "../../../../modules/configuration-hub/data/models/configuration-hub.model";
import { UnifiedDropdown } from "../../../../modules/_shared/components";
import { buildGdxApiUrl } from "../../../../common/utils/api.util";
import "./AssignmentMainContent.scss";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PanToolOutlinedIcon from "@mui/icons-material/PanToolOutlined";
import { formattedDate } from "../../../utils/date";
import { useAppSelector } from "../../../../common/redux/core";

export interface AssignmentItem {
  id: number;
  title: string;
  status: string;
  company: string;
  department: string;
  createdBy: string;
  createdOn: string;
  userQuery?: string;
  documentType?: string;
  inreviewflag?: boolean;
  approvalflag?: boolean;
  completedflag?: boolean;
  // Optional additional fields for tag library
  guidCode?: string;
  description?: string;
  cfiHOSId?: string;
  isCFIHOS?: boolean;
  order?: number;
  configurationName?: string;
}

export interface DropdownOption {
  value: string;
  label: string;
}

interface AssignmentMainContentProps {
  assignments: AssignmentItem[];
  configurationList: AssignmentItem[];
  setConfigurationList: React.Dispatch<React.SetStateAction<AssignmentItem[]>>;
  // Dropdown configuration (for Processing Hub)
  showDropdown?: boolean;
  dropdownLabel?: string;
  dropdownValue?: string;
  dropdownOptions?: DropdownOption[];
  onDropdownChange?: (value: string) => void;

  // New configuration button (for Configuration Hub)
  showNewConfigButton?: boolean;
  newConfigButtonText?: string;
  onNewConfigClick?: () => void;

  // Document type filter (for filtering cards by document type)
  showDocumentTypeFilter?: boolean;
  onDocumentTypeChange?: (value: string) => void;

  // Card behavior
  cardsClickable?: boolean;
  onCardClick?: (assignment: AssignmentItem) => void;

  // Actions
  showActions?: boolean;
  showEditAction?: boolean;
  showDeleteAction?: boolean;
  showViewAction?: boolean;
  showUploadAction?: boolean;
  showCreateFlowBtn?: boolean;
  showProcessingHubBtn?: boolean;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  onView?: (id: number) => void;
  onUpload?: (id: number) => void;
  onCreateFlow?: (id: number) => void;
  onProcessingHub?: (id: number) => void;
  // Per-item action visibility functions
  canEdit?: (item: AssignmentItem) => boolean;
  canDelete?: (item: AssignmentItem) => boolean;
  canView?: (item: AssignmentItem) => boolean;

  // Header configuration
  headerTitle?: string;
  // Loading state
  isLoading?: boolean;

  // Card label customization
  companyLabel?: string;
  departmentLabel?: string;
  createdByLabel?: string;
  createdOnLabel?: string;
  userQueryLabel?: string;
  showStatus?: boolean;

  // Field visibility
  showCompanyField?: boolean;
  showDepartmentField?: boolean;
  showCreatedByField?: boolean;
  showCreatedOnField?: boolean;
  showUserQueryField?: boolean;

  // Styling
  className?: string;
}

const AssignmentMainContent: React.FC<AssignmentMainContentProps> = ({
  assignments,
  configurationList,
  setConfigurationList,
  showDropdown = false,
  dropdownLabel = "Document type",
  dropdownValue,
  dropdownOptions = [],
  onDropdownChange,
  showNewConfigButton = false,
  newConfigButtonText = "New configuration",
  onNewConfigClick,
  showDocumentTypeFilter = false,
  onDocumentTypeChange,
  cardsClickable = false,
  onCardClick,
  showActions = false,
  showEditAction = true,
  showDeleteAction = true,
  showViewAction = true,
  showUploadAction = false,
  showCreateFlowBtn = false,
  showProcessingHubBtn = false,
  onEdit,
  onDelete,
  onView,
  onUpload,
  onCreateFlow,
  onProcessingHub,
  canEdit,
  canDelete,
  canView,
  headerTitle = "Available flow configurations",
  isLoading = false,
  companyLabel = "Company",
  departmentLabel = "Department",
  createdByLabel = "Created by",
  createdOnLabel = "Created on",
  userQueryLabel = "Query",
  showStatus = false,
  showCompanyField = true,
  showDepartmentField = true,
  showCreatedByField = true,
  showCreatedOnField = true,
  showUserQueryField = false,
  className = ""
}) => {
  const navigate = useNavigate();
  const [isLoadingConfigurations, setIsLoadingConfigurations] = useState(false);
  const [configurationsError, setConfigurationsError] = useState<string | null>(
    null
  );

  // Use global Redux state for document type
  const { selectedDocumentType } = useAppSelector(
    (state) => state.documentType
  );

  // Function to fetch database configurations by document type
  const fetchDatabaseConfigurations = async (documentType: string) => {
    setIsLoadingConfigurations(true);
    setConfigurationsError(null);

    try {
      // Use the same API as Flow Configuration
      const response = await axios.get(
        buildGdxApiUrl(
          `gdx/database/document_type_information/get/${documentType}`
        )
      );

      if (response && response.data) {
        // Transform configurations to AssignmentItem format (same as Flow Config)
        const transformedConfigurations: AssignmentItem[] = response.data.map(
          (config: any) => {
            return {
              id: config.flow_id || config.id,
              title: config.flow_name,
              status: config.data_exposer || determineStatus(config),
              company: "EY",
              department: "Technology",
              createdBy: config.created_by,
              createdOn: formatDate(config.created_time),
              documentType: documentType
            };
          }
        );

        setConfigurationList(transformedConfigurations);
      } else {
        setConfigurationsError("Failed to fetch configurations");
      }
    } catch (error) {
      console.error("Processing Hub: Error fetching configurations:", error);
      setConfigurationsError("Error fetching configurations");
    } finally {
      setIsLoadingConfigurations(false);
    }
  };

  useEffect(() => {
    if (dropdownValue === "Contract") {
      fetchDatabaseConfigurations("Contract");
    } else if (dropdownValue === "Engineering") {
      fetchDatabaseConfigurations("Engineering");
    } else if (dropdownValue === "Invoice") {
      fetchDatabaseConfigurations("Invoice");
    } else if (dropdownValue === "Others") {
      fetchDatabaseConfigurations("Others");
    }
  }, [dropdownValue]);

  // Helper functions (same as FlowConfigService)
  const determineStatus = (config: any): string => {
    if (config.data_exposer) {
      return config.data_exposer;
    }

    const enabledOperations = [
      config.ingestion,
      config.extraction,
      config.tagging,
      config.similarity,
      config.export
    ].filter(Boolean).length;

    if (enabledOperations === 0) {
      return "Draft";
    } else if (enabledOperations < 3) {
      return "In Progress";
    } else {
      return "Active";
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      return formattedDate(dateString);
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString || "";
    }
  };

  const handleCardClick = (assignment: AssignmentItem) => {
    if (cardsClickable && onCardClick) {
      onCardClick(assignment);
    }
  };

  const handleEdit = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(id);
    }
  };

  const handleDelete = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(id);
    }
  };

  const handleView = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (onView) {
      onView(id);
    }
  };

  const handleUpload = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (onUpload) {
      onUpload(id);
    }
  };

  const handleCreateFlow = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (onCreateFlow) {
      onCreateFlow(id);
    }
  };

  const handleProcessingHub = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (onProcessingHub) {
      onProcessingHub(id);
    }
  };

  // Handle document type filter change
  const handleDocumentTypeChange = (value: string) => {
    // Global Redux state is updated by UnifiedDropdown component
    if (onDocumentTypeChange) {
      onDocumentTypeChange(value);
    }
  };

  // Filter configurations based on selected document type
  const filteredConfigurations = useMemo(() => {
    if (!showDocumentTypeFilter) {
      return configurationList;
    }

    if (selectedDocumentType === "All" || selectedDocumentType === "") {
      return configurationList;
    }
    const filtered = configurationList.filter(
      (config) =>
        config.documentType?.toString() === selectedDocumentType.toString()
    );

    return filtered;
  }, [configurationList, selectedDocumentType, showDocumentTypeFilter]);

  return (
    <div className={`assignment-main-content ${className}`}>
      {/* Document Type Dropdown - Only for Processing Hub */}
      {showDropdown && (
        <div className="document-type-section">
          <label htmlFor="document-type">{dropdownLabel}</label>
          <select
            id="document-type"
            value={dropdownValue}
            onChange={(e) => onDropdownChange?.(e.target.value)}
            className="document-type-select"
          >
            {dropdownOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Available Configurations Header */}
      {headerTitle !== "" && (
        <div className="configurations-header">
          <h2>{headerTitle}</h2>

          {/* Document Type Filter Dropdown - Controlled by showDocumentTypeFilter prop */}
          {showDocumentTypeFilter && (
            <div className="document-type-filter">
              <UnifiedDropdown
                type="document-type"
                useRedux={true}
                onChange={handleDocumentTypeChange}
                label=""
                // placeholder="Filter by document type"
                className="filter-dropdown"
              />
            </div>
          )}

          {/* Preview Tools button - only show in extraction configuration */}
          {headerTitle?.toLowerCase().includes("extraction") && (
            <button
              className="preview-tools-btn"
              onClick={() => navigate(MainRoutes.AVAILABLE_TOOLS)}
            >
              <svg
                className="preview-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{
                  width: "1rem",
                  height: "1rem",
                  marginRight: "0.5rem"
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Preview Tools
            </button>
          )}

          {/* New Configuration button */}
          {showNewConfigButton && (
            <button
              className="new-configuration-btn"
              onClick={onNewConfigClick}
            >
              <svg
                className="plus-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{
                  width: "1rem",
                  height: "1rem",
                  marginRight: "0.5rem"
                }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {newConfigButtonText}
            </button>
          )}
        </div>
      )}

      {/* Assignment Cards */}
      <div className="assignment-cards">
        {isLoading || isLoadingConfigurations ? (
          <div className="loading-state">
            <p>Loading configurations...</p>
          </div>
        ) : configurationsError ? (
          <div className="error-state">
            <p>Error: {configurationsError}</p>
            {dropdownValue === "Contract" && (
              <button
                onClick={() => fetchDatabaseConfigurations("NE")}
                className="retry-btn"
              >
                Retry
              </button>
            )}
          </div>
        ) : filteredConfigurations.length === 0 ? (
          <div className="empty-state">
            <p>No configurations available for the selected document type</p>
          </div>
        ) : (
          filteredConfigurations.map((assignment) => (
            <div
              key={assignment.id}
              className={`assignment-card ${cardsClickable ? "clickable" : ""}`}
              onClick={() => handleCardClick(assignment)}
              style={{ cursor: cardsClickable ? "pointer" : "default" }}
            >
              <div className="card-header">
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore  */}
                <FaRegFileAlt className="document-icon" />
                <h3 className="card-title">{assignment.title}</h3>
              </div>

              {/* Two column layout */}
              <div className="card-content">
                <div className="content-grid">
                  {(showCompanyField ||
                    showCreatedByField ||
                    showUserQueryField) && (
                    <div className="left-column">
                      {showCompanyField && (
                        <div className="info-item">
                          <span className="info-label">{companyLabel} :</span>
                          <span className="info-value">EY</span>
                        </div>
                      )}
                      {showCreatedByField && (
                        <div className="info-item">
                          <span className="info-label">{createdByLabel} :</span>
                          <span className="info-value">
                            {assignment.createdBy}
                          </span>
                        </div>
                      )}
                      {showUserQueryField && assignment.userQuery && (
                        <div className="info-item">
                          <span className="info-label">{userQueryLabel} :</span>
                          <span
                            className="info-value user-query-value"
                            title={assignment.userQuery}
                          >
                            {assignment.userQuery}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {(showDepartmentField || showCreatedOnField) && (
                    <div className="right-column">
                      {showDepartmentField && (
                        <div className="info-item">
                          <span className="info-label">
                            {departmentLabel} :
                          </span>
                          <span className="info-value">Technology</span>
                        </div>
                      )}
                      {showCreatedOnField && (
                        <div className="info-item">
                          <span className="info-label">{createdOnLabel} :</span>
                          <span className="info-value">
                            {assignment.createdOn}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Status inner card */}
              {showStatus && (
                <div className="status-inner-card">
                  <span
                    className={`status-chip status-${assignment.status
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    {assignment.status.toLowerCase() === "running" && (
                      <CircularProgress
                        size={14}
                        thickness={5}
                        color="inherit"
                      />
                    )}
                    {assignment.status.toLowerCase() === "hitl_required" && (
                      <PanToolOutlinedIcon sx={{ fontSize: 14 }} />
                    )}
                    {assignment.status.toLowerCase() === "complete" && (
                      <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                    )}
                    {assignment.status.toLowerCase() === "error" && (
                      <ErrorOutlineIcon sx={{ fontSize: 14 }} />
                    )}
                    {![
                      "running",
                      "hitl_required",
                      "complete",
                      "error"
                    ].includes(assignment.status.toLowerCase()) && (
                      <span className="status-icon">↻</span>
                    )}
                    {assignment.status}
                  </span>
                </div>
              )}

              {/* Action buttons */}
              {showActions && (
                <div className="card-actions">
                  {showCreateFlowBtn && (
                    <button
                      className="view-btn"
                      onClick={(e) => handleCreateFlow(e, assignment.id)}
                      title="Create flow with this configuration"
                    >
                      <CreateNewFolderOutlinedIcon />
                    </button>
                  )}
                  {showProcessingHubBtn && (
                    <button
                      className="view-btn"
                      onClick={(e) => handleProcessingHub(e, assignment.id)}
                      title="Open processing hub"
                    >
                      {/* @ts-ignore  */}
                      <FaDatabase />
                    </button>
                  )}
                  {showViewAction && (!canView || canView(assignment)) && (
                    <button
                      className="view-btn"
                      onClick={(e) => handleView(e, assignment.id)}
                      title="View details"
                    >
                      <VisibilityOutlined />
                    </button>
                  )}
                  {showEditAction && (!canEdit || canEdit(assignment)) && (
                    <button
                      className="edit-btn"
                      onClick={(e) => handleEdit(e, assignment.id)}
                      title="Edit Configuration"
                    >
                      <EditOutlinedIcon />
                    </button>
                  )}
                  {showDeleteAction &&
                    (!canDelete || canDelete(assignment)) && (
                      <button
                        className="delete-btn"
                        onClick={(e) => handleDelete(e, assignment.id)}
                        title="Delete Configuration"
                      >
                        <DeleteOutlined />
                      </button>
                    )}
                  {showUploadAction && (
                    <button
                      className="upload-btn"
                      onClick={(e) => handleUpload(e, assignment.id)}
                      title="Upload Document"
                    >
                      <FileUploadOutlinedIcon />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AssignmentMainContent;
